"use client"

import { Skeleton, Stack } from "@mui/material"
import { Section } from "@tc/ui-dfe/components"
import { RecentExportRequest } from "@tc/ui-dfe/containers/ExportRequest"
import { ViewAllRequestProvider } from "@tc/ui-dfe/hooks"
import { Permission } from "@tc/ui-permission/components"
import { usePermission } from "@tc/ui-permission/hooks"
import { PERMISSION_CODE } from "@tc/ui-permission/utils"
import {
  SearchFieldCollectionMappings,
  SearchFieldId,
  useTypesenseClient,
  useUiTypesenseClientProps,
} from "@tc/ui-typesense-client"
import dynamic from "next/dynamic"
import React, { useEffect } from "react"
import ReactDOM from "react-dom"
import { InstantSearch } from "react-instantsearch"

// Lazy load modal
const TermsAndConditionsModal = dynamic(
  () =>
    import("@tc/ui-dfe/components").then((mod) => mod.TermsAndConditionsModal),
  { ssr: false },
)

export const RecentRequestsSkeleton = () => (
  <Section title="Recent requests" sx={{ pt: 1.5 }}>
    <Stack spacing={1}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={48} />
      ))}
    </Stack>
  </Section>
)

export function HomeClientSections() {
  const { hasAccess } = usePermission()
  useEffect(() => {
    const a11yCheck = async () => {
      if (process.env.NODE_ENV === "development") {
        const axe = await import("@axe-core/react")
        axe.default(React, ReactDOM, 1000)
      }
    }
    a11yCheck()
  }, [])

  const searchParams: object = {
    query_by:
      "certificate_number, departure_date, modified_at, request_name, country_name, organisation, template_code, unofficial_information, product_descriptions, product_container_numbers, product_seals, product_NZHSC_codes, product_productionBatch_ids, product_botanical_name, unique_consignment_ref, product_item_marks, product_unofficial_commercial_ref, product_unofficial_commercial_info, local_carrier, carrier_name, conveyance_reference, destination_country, destination_country_name, org_cert_ref_code, vessel, sub_class, class, status_label",
    query_by_weights: "2,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1",
    num_typos: "2,0,0,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2",
    typoTokensThreshold: 0,
    page: 50,
    sort_by: "modified_ts:desc",
  }
  const typeSenseProps: useUiTypesenseClientProps = {
    searchParams: searchParams,
    collectionId: SearchFieldId.ExportCertificate,
    context: "exports",
    usePermission: true,
  }
  const { adapter } = useTypesenseClient(typeSenseProps)
  const collectionName =
    SearchFieldCollectionMappings[SearchFieldId.ExportCertificate]

  return (
    <>
      <Permission
        allow={[
          PERMISSION_CODE.EXPORT_REQUEST_VIEW,
          PERMISSION_CODE.EXPORT_REQUEST_LIST,
        ]}
      >
        {adapter && adapter?.searchClient ? (
          <InstantSearch
            searchClient={adapter?.searchClient}
            indexName={collectionName}
            routing
            future={{
              preserveSharedStateOnUnmount: true,
            }}
          >
            <ViewAllRequestProvider
              adapter={adapter}
              typeSenseProps={typeSenseProps}
            >
              <RecentExportRequest />
            </ViewAllRequestProvider>
          </InstantSearch>
        ) : (
          <RecentRequestsSkeleton />
        )}
      </Permission>
      <TermsAndConditionsModal />
    </>
  )
}
